'use client'

import dynamic from 'next/dynamic'
import { Box, Grid, Typography } from '@mui/material'

// Types
import type { SignupFormProps } from '@/types/components/organisms/forms/signup'

// Components
import TurnstileFieldMolecule from '@/components/molecules/field/turnstile'

const TextFieldMolecule = dynamic(() => import('@/components/molecules/field/text'))
const ChipsFieldMolecule = dynamic(() => import('@/components/molecules/field/chips'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))

// Compact sign-up form that fits one screen: contact and business fields in rows of three, short choices as chips
const SignupFormOrganism = (props: SignupFormProps) => {
	// Props
	const { lng, locale, control, loading, turnstileRef, onSubmit } = props

	// Variables
	const { fields, submit, requiredNote } = locale.content.signup.form
	const text = { lng, locale, control, size: 'small' as const }
	const chips = { control, dense: true }

	return (
		<Grid
			component="form"
			container
			spacing={1.5}
			onSubmit={event => {
				event.preventDefault()
				onSubmit()
			}}
			noValidate
		>
			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="full_name" label={fields.fullName} required />
			</Grid>

			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="mobile" type="mobile" pattern="mobile" label={fields.mobile} helperText={fields.mobileHelper} required />
			</Grid>

			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="email" type="email" pattern="email" label={fields.email} helperText={fields.emailHelper} />
			</Grid>

			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="business_name" label={fields.businessName} required />
			</Grid>

			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="city" label={fields.city} required />
			</Grid>

			<Grid size={{ xs: 12, sm: 4 }}>
				<TextFieldMolecule {...text} name="current_software" label={fields.currentSoftware} />
			</Grid>

			<Grid size={{ xs: 12, md: 6 }} sx={{ mt: 0.5 }}>
				<ChipsFieldMolecule {...chips} name="business_type" label={fields.businessType.label} options={fields.businessType.options} required />
			</Grid>

			<Grid size={{ xs: 12, md: 6 }} sx={{ mt: 0.5 }}>
				<ChipsFieldMolecule {...chips} name="branches" label={fields.branches.label} options={fields.branches.options} required />
			</Grid>

			<Grid size={12} sx={{ mt: 0.5 }}>
				<ChipsFieldMolecule {...chips} name="needs" label={fields.needs.label} options={fields.needs.options} multiple required />
			</Grid>

			<Grid size={{ xs: 12, md: 5 }} sx={{ mt: 0.5 }}>
				<ChipsFieldMolecule {...chips} name="contact_time" label={fields.contactTime.label} options={fields.contactTime.options} />
			</Grid>

			<Grid size={{ xs: 12, md: 7 }} sx={{ mt: 0.5 }}>
				<ChipsFieldMolecule {...chips} name="source" label={fields.source.label} options={fields.source.options} />
			</Grid>

			<Grid size={12} sx={{ mt: 0.5 }}>
				<TextFieldMolecule {...text} name="description" label={fields.description} multiline rows={2} />
			</Grid>

			<Grid size={12} sx={{ mt: 0.5 }}>
				<TurnstileFieldMolecule ref={turnstileRef} lng={lng} action="signup" />
			</Grid>

			<Grid size={12} sx={{ mt: 0.5 }}>
				<Box sx={{ '& .MuiButtonBase-root': { width: 1 } }}>
					<PrimaryButtonAtom text={submit} type="submit" color="primary" size="medium" loading={loading} />
				</Box>

				<Typography sx={{ mt: 1, fontSize: 12.5, color: 'text.secondary', textAlign: 'center' }}>{requiredNote}</Typography>
			</Grid>
		</Grid>
	)
}

export default SignupFormOrganism
